import { useEffect, useRef } from 'react';
import { log } from 'log';
import { pick } from 'ramda';
import { useDispatch } from 'react-redux';
import { htcmdService } from 'js/services/htcmd.service';
import { useNavigate } from 'js/hooks/navigate/useNavigate';
import { isExternalUrl, openWindow } from 'js/utils/url.util';
import { useLoginPopup } from 'js/components/popups/LoginPopup';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { setPopupDynamicPagePath } from 'js/components/system/Page/store/actions';
import { useAppSelector, useUserSelector, useLottoSelector } from 'js/store/hooks';
import { useAddSubscription, useGetPlayerTags, usePushSubscription } from 'server';

import { useLottoGetTemptoken } from './useLottoGetTemptoken';

export const useLottoListeners = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const showLoginPopup = useLoginPopup();
    const lottoGetTemptoken = useLottoGetTemptoken();
    const {
        isSessionStarted,
        windowSessionId,
        isLoggedIn
    } = useUserSelector(pick(['isSessionStarted', 'windowSessionId', 'isLoggedIn']));
    const { depositPage, loginPage, lottoRootURL } = useAppSelector(pick(['depositPage', 'loginPage', 'lottoRootURL']));
    const { isAppInitialized, isRenderingStarted } = useLottoSelector(pick(['isAppInitialized', 'isRenderingStarted']));
    const addSubscription = useAddSubscription();
    const getPlayerTags = useGetPlayerTags();
    const lottoTagsRef = useRef<string[]>([]);

    useEffect(() => {
        if (!isAppInitialized || !isRenderingStarted) {
            return;
        }

        try {
            window.Lotto?.setListener('SHOW_LOGIN', (...props) => {
                if (isLoggedIn) {
                    window.Lotto?.sendMessage('SYNC_SESSION');
                } else if (htcmdService.isEnabled) {
                    htcmdService.openLogin();
                } else {
                    if (loginPage) {
                        navigate(loginPage);
                    } else {
                        showLoginPopup(props);
                    }
                }
            });
            window.Lotto?.setListener('INTERNAL_ROUTE', (route) => dispatch(setPopupDynamicPagePath(route || lottoRootURL)));
            window.Lotto?.setListener('GET_SESSION_INFO', lottoGetTemptoken);
            window.Lotto?.setListener('ANALYTICS_EVENT', (event) => publicEventsAPIService.trigger('lotto-analytics', event));
        } catch (error) {
            log('log', '[LOTTO | Portal] Set listeners error', error);
        }
    }, [
        dispatch,
        isRenderingStarted,
        isAppInitialized,
        showLoginPopup,
        lottoRootURL,
        lottoGetTemptoken,
        isLoggedIn,
        navigate,
        loginPage
    ]);

    useEffect(() => {
        if (!isAppInitialized) {
            return;
        }

        try {
            window.Lotto?.setListener('SHOW_DEPOSIT', () => {
                if (htcmdService.isEnabled) {
                    htcmdService.openDeposit();
                } else {
                    navigate(depositPage);
                }
            });
        } catch (error) {
            log('log', '[LOTTO | Portal] Set listeners error', error);
        }
    }, [depositPage, isAppInitialized, navigate]);

    useEffect(() => {
        if (!isAppInitialized) {
            return;
        }

        try {
            window.Lotto?.setListener('NAVIGATE', (url, options) => {
                if (url.startsWith('native://')) {
                    htcmdService.sendMessage(url.substr(9), options);

                    return;
                }

                if (isExternalUrl(url) && htcmdService.isEnabled) {
                    htcmdService.openExternalPage(url);

                    return;
                }

                switch (options?.target) {
                    case '_blank':
                        openWindow(url);
                        break;

                    default:
                        navigate(url);
                }
            });
        } catch (error) {
            log('log', '[LOTTO | Portal] Set listeners error', error);
        }
    }, [isAppInitialized, navigate]);

    useEffect(() => {
        if (!isSessionStarted || !isAppInitialized || !isRenderingStarted) {
            return;
        }

        window.Lotto?.setListener('IMS_GET_TAGS', async (tagNames) => {
            await addSubscription({
                windowSessionId: windowSessionId ?? -1,
                subscriptionTags: tagNames
            });

            const { tags } = await getPlayerTags({ tagNames });

            window.Lotto?.sendMessage('IMS_SET_TAGS', tags);

            lottoTagsRef.current = tagNames;
        });
    }, [
        isRenderingStarted,
        lottoTagsRef,
        isAppInitialized,
        isSessionStarted,
        windowSessionId,
        getPlayerTags,
        addSubscription
    ]);

    /**
     * This listener is used to handle player tag changes, but it triggers message to Lotto
     * only in case if the tag name is in the list of tag names that were requested before.
     */
    usePushSubscription(31133, () => (pushMessage) => {
        if (!isAppInitialized || !isRenderingStarted) {
            return;
        }

        const { notificationPlayerTagChangeInfo } = pushMessage?.data?.notification || {};

        if (!notificationPlayerTagChangeInfo) {
            return;
        }

        const {
            tag,
            value,
            removed
        } = notificationPlayerTagChangeInfo;

        if (!lottoTagsRef.current.includes(tag)) {
            return;
        }

        window.Lotto?.sendMessage('IMS_UPDATE_TAGS', [
            {
                tag,
                value,
                removed
            }
        ]);
    }, [
        isRenderingStarted,
        isAppInitialized,
        lottoTagsRef
    ]);
};
